import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
    Editor,
    EditorState,
    ContentState,
    RichUtils,
    Entity,
    convertFromHTML,
    Modifier,
    CompositeDecorator,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { SuiTextInput } from '@splashthat/sui';
import Bold from '@splashthat/doppler/icons/Bold';
import Italic from '@splashthat/doppler/icons/Italic';
import Underline from '@splashthat/doppler/icons/Underline';
import Link from '@splashthat/doppler/icons/Link';
import Trash from '@splashthat/doppler/icons/Trash';
import Check from '@splashthat/doppler/icons/Check';

import { RESOURCE_URL_PLACEHOLDER, makeValidAttachmentUrl } from '../../utils/urlValidatiorHelper';

import CharacterMaxCount from './CharacterMaxCount';
import CharacterCountdown from './CharacterCountdown';
import './DraftTextEditor.styl';

export const LABEL_MAX_CHARACTER_LENGTH = 2000;
const BOLD = 'BOLD';
const ITALIC = 'ITALIC';
const UNDERLINE = 'UNDERLINE';
const LINK = 'LINK';
const MUTABLE = 'MUTABLE';
const BASE_CLASS_NAME = 'DraftTextEditor';

const IconWrapper = styled.div`
    color: ${({ isActive, theme }) => (isActive ? theme.palette.grayscale.gray65 : theme.palette.grayscale.gray50)};
    pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
    cursor: pointer;

    &:active,
    &:hover {
        color: ${({ theme }) => theme.palette.grayscale.gray65};
    }

    &[disabled] {
        opacity: 0.5;
    }
`;

export default class DraftTextEditor extends React.Component {
    constructor(props) {
        super(props);

        this.btnRef = React.createRef();
        this.editor = React.createRef();

        const decorator = new CompositeDecorator([
            {
                strategy: this.findLinkEntities,
                component: this.Link,
            },
        ]);

        const { value, defaultValue } = this.props;

        const initialValue = value || defaultValue;

        const contentState = initialValue ? this.createContentStateFromText(initialValue) : null;

        this.state = {
            editorState: contentState
                ? EditorState.createWithContent(contentState, decorator)
                : EditorState.createEmpty(decorator),
            showURLInput: false,
            urlValue: '',
        };
    }

    static propTypes = {
        value: PropTypes.string,
        onInput: PropTypes.func,
        defaultValue: PropTypes.string,
        onChange: PropTypes.func,
        disableBold: PropTypes.bool,
        disableLink: PropTypes.bool,
        disableCharacterCount: PropTypes.bool,
        /** called readOnly instead of disabled because that's the prop name of draft-js/Editor  */
        readOnly: PropTypes.bool,
        /** By default, every block of text coming from the editor is wrapped in <p> tags, even if there's only a single line with no line breaks. */
        /** Setting this property to false makes single line texts to NOT be wrapped in <p> tags. Multi-paragraph texts are wrapped as usual. */
        wrapSingleLineAsParagraph: PropTypes.bool,
        maxByteLimit: PropTypes.number,
        enableCharacterCountdown: PropTypes.bool,
    };

    static defaultProps = {
        value: null,
        onInput: null,
        defaultValue: null,
        onChange: null,
        disableBold: false,
        disableLink: false,
        disableCharacterCount: false,
        readOnly: false,
        wrapSingleLineAsParagraph: true,
        maxByteLimit: null,
        enableCharacterCountdown: false,
    };

    createContentStateFromText(text) {
        const blocksFromHTML = convertFromHTML(text);
        if (blocksFromHTML && blocksFromHTML.contentBlocks) {
            return ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
        }
        return null;
    }

    onChange = (editorState) => {
        this.setState({ editorState });
        this.handleChangeEvent(editorState);
    };

    handleChangeEvent = (editorState) => {
        const currentContent = editorState.getCurrentContent();
        const plainText = currentContent.getPlainText().trim();
        const options = {
            /** Sets the HTML tag used to wrap text blocks; <p> is the default. If set to null, block texts are not wrapped. */
            /** See the comments for the wrapSingleLineAsParagraph property above for more details. */
            defaultBlockTag:
                this.props.wrapSingleLineAsParagraph || editorState.getCurrentContent().getBlocksAsArray().length > 1
                    ? 'p'
                    : null,
        };
        const html = stateToHTML(currentContent, options);
        if (this.props.onChange) {
            this.props.onChange(
                undefined,
                {
                    label: plainText,
                    labelMarkup: !plainText ? '' : html,
                },
                currentContent.hasText(),
            );
        }
    };

    handleInputEvent = (event) => {
        const { value } = event.target;

        if (this.props.onInput) {
            this.props.onInput(event, value);
        }
    };

    toggleStyle = (style) => (e) => {
        e.preventDefault();
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, style));
    };

    renderButtons = (editorState) => {
        const { disableBold, disableLink, readOnly } = this.props;
        const iconClassName = `${BASE_CLASS_NAME}__public-content--icon`;
        const urlInput = this.state.showURLInput && this.renderUrlInput();

        const currentStyle = editorState.getCurrentInlineStyle();

        const buttons = [
            {
                label: 'bold',
                style: BOLD,
                icon: Bold,
                disabled: disableBold,
            },
            {
                label: 'italic',
                style: ITALIC,
                icon: Italic,
            },
            {
                label: 'underline',
                style: UNDERLINE,
                icon: Underline,
            },
        ];

        return (
            <div
                className={[
                    `${BASE_CLASS_NAME}__public-content`,
                    urlInput && `${BASE_CLASS_NAME}__public-content--right`,
                    readOnly && `${BASE_CLASS_NAME}__public-content--read-only`,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {urlInput || (
                    <React.Fragment>
                        {buttons.map((button) => (
                            <IconWrapper
                                key={button.label}
                                isActive={currentStyle.has(button.style)}
                                disabled={readOnly}
                            >
                                {!button.disabled && (
                                    <button.icon
                                        className={`${iconClassName} ${BASE_CLASS_NAME}__${button.label}`}
                                        onMouseDown={this.toggleStyle(button.style)}
                                        data-qa={`${BASE_CLASS_NAME}__${button.label}`}
                                    />
                                )}
                            </IconWrapper>
                        ))}
                        {!disableLink && (
                            <IconWrapper disabled={readOnly}>
                                <Link
                                    className={`${iconClassName} ${BASE_CLASS_NAME}__link`}
                                    onMouseDown={this.promptForLink}
                                    data-qa={`${BASE_CLASS_NAME}__link`}
                                />
                            </IconWrapper>
                        )}
                    </React.Fragment>
                )}
            </div>
        );
    };

    renderUrlInput = () => (
        <React.Fragment>
            <SuiTextInput
                onChange={this.onURLChange}
                ref={this.btnRef}
                type={'text'}
                value={this.state.urlValue}
                placeholder={RESOURCE_URL_PLACEHOLDER}
                onKeyDown={this.onLinkInputKeyDown}
                data-qa={`${BASE_CLASS_NAME}__link__text-input`}
            />

            <IconWrapper>
                <Check
                    className={`${BASE_CLASS_NAME}__public-content--add`}
                    onMouseDown={this.confirmLink}
                    data-qa={`${BASE_CLASS_NAME}__link--confirm`}
                />
            </IconWrapper>
            <IconWrapper>
                <Trash
                    className={`${BASE_CLASS_NAME}__public-content--remove`}
                    onMouseDown={this.removeLink}
                    data-qa={`${BASE_CLASS_NAME}__link--delete`}
                />
            </IconWrapper>
        </React.Fragment>
    );

    /*
        Helper methods for adding links
        https://github.com/facebook/draft-js/blob/master/examples/draft-0-9-1/link/link.html
    */
    findLinkEntities = (contentBlock, callback) => {
        contentBlock.findEntityRanges((character) => {
            const entityKey = character.getEntity();
            return entityKey !== null && Entity.get(entityKey).getType() === LINK;
        }, callback);
    };

    Link = (props) => {
        const { url } = Entity.get(props.entityKey).getData();

        return <a href={url}>{props.children}</a>;
    };

    onURLChange = (event) => {
        this.setState({ urlValue: event.target.value });
    };

    promptForLink = (event) => {
        event.preventDefault();
        const { editorState } = this.state;
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            const contentState = editorState.getCurrentContent();
            const startKey = editorState.getSelection().getStartKey();
            const startOffset = editorState.getSelection().getStartOffset();
            const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
            const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

            const linkInstance = linkKey && contentState.getEntity(linkKey);
            const url = linkKey ? linkInstance.getData().url : '';
            this.setState(
                {
                    showURLInput: true,
                    urlValue: url,
                },
                () => {
                    setTimeout(() => this.btnRef.current.focus(), 0);
                },
            );
        }
    };

    onLinkInputKeyDown = (event) => {
        // when user clicks Enter
        if (event.which === 13) {
            this.confirmLink(event);
        }
    };

    confirmLink = (event) => {
        event.preventDefault();
        const { editorState, urlValue } = this.state;
        const newTab = !urlValue.startsWith('#');
        const target = newTab ? '_blank' : '_self';
        const entityKey = Entity.create(LINK, MUTABLE, {
            url: makeValidAttachmentUrl(urlValue),
            target,
        });
        this.setState(
            {
                editorState: RichUtils.toggleLink(editorState, editorState.getSelection(), entityKey),
                showURLInput: false,
                urlValue: '',
            },
            () => {
                setTimeout(() => this.editor.current.focus(), 0);
            },
        );
    };

    removeLink = (event) => {
        event.preventDefault();
        const { editorState } = this.state;
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            this.setState(
                {
                    editorState: RichUtils.toggleLink(editorState, selection, null),
                    showURLInput: false,
                    urlValue: '',
                },
                () => {
                    setTimeout(() => this.editor.current.focus(), 0);
                },
            );
        }
    };

    getByteSize = (text) => {
        const textEncoder = new TextEncoder();
        const bytes = textEncoder.encode(text).length;

        return bytes;
    };

    isLimitReached = (contentMarkup, pastedText = '') => {
        if (this.props.maxByteLimit !== null) {
            return this.getByteSize(contentMarkup + pastedText) >= this.props.maxByteLimit;
        }
        const currentContentLength = contentMarkup.length;
        return currentContentLength + pastedText.length >= LABEL_MAX_CHARACTER_LENGTH;
    };

    // https://stackoverflow.com/questions/46044251/how-to-limit-max-length-of-draft-js
    handleBeforeInput = () => {
        const currentContent = this.state.editorState.getCurrentContent();
        const currentContentMarkup = stateToHTML(currentContent);

        return this.isLimitReached(currentContentMarkup);
    };

    handlePastedText = (pastedText, html, editorState) => {
        const currentContent = editorState.getCurrentContent();
        const currentContentMarkup = stateToHTML(currentContent);
        const charLimitReached = this.isLimitReached(currentContentMarkup, pastedText);

        if (!charLimitReached) {
            const newState = Modifier.insertText(
                editorState.getCurrentContent(),
                editorState.getSelection(),
                pastedText,
            );

            // paste only plain text
            this.onChange(EditorState.push(editorState, newState, 'insert-fragment'));
        }

        return true;
    };

    render() {
        const { value, defaultValue, readOnly } = this.props;

        const propValue = typeof value === 'string' ? value : defaultValue;

        const labelLength = this.props.maxByteLimit ? this.getByteSize(propValue || '') : (propValue || '').length;

        return (
            <div className={BASE_CLASS_NAME} data-qa={BASE_CLASS_NAME}>
                <div role="presentation" onClick={() => this.editor.current.focus()}>
                    <Editor
                        editorState={this.state.editorState}
                        onChange={this.onChange}
                        ref={this.editor}
                        onInput={this.handleInputEvent}
                        handleBeforeInput={this.handleBeforeInput}
                        handlePastedText={this.handlePastedText}
                        readOnly={readOnly}
                    />
                </div>
                {!this.props.disableCharacterCount &&
                    (this.props.enableCharacterCountdown ? (
                        <CharacterCountdown
                            labelLength={labelLength}
                            maxLength={this.props.maxByteLimit ?? LABEL_MAX_CHARACTER_LENGTH}
                        />
                    ) : (
                        <CharacterMaxCount
                            labelLength={labelLength}
                            maxLength={this.props.maxByteLimit ?? LABEL_MAX_CHARACTER_LENGTH}
                        />
                    ))}
                {this.renderButtons(this.state.editorState)}
            </div>
        );
    }
}
